/**
 * Google Analytics Service
 * 
 * Provides utilities for tracking events and conversions in Google Analytics
 */

class GoogleAnalyticsService {
  constructor() {
    this.isEnabled = typeof window !== 'undefined' && window.gtag;
    this.trackingId = 'AW-17428001565';
  }

  /**
   * Track a custom event
   * @param {string} action - The action being tracked
   * @param {string} category - The category of the event
   * @param {string} label - Optional label for the event
   * @param {number} value - Optional numerical value
   */
  trackEvent(action, category, label = null, value = null) {
    if (!this.isEnabled) {
      console.log('📊 GA Event (Dev):', { action, category, label, value });
      return;
    }

    const eventData = {
      event_category: category,
      event_label: label,
      value: value
    };

    // Remove null values
    Object.keys(eventData).forEach(key => {
      if (eventData[key] === null) {
        delete eventData[key];
      }
    });

    window.gtag('event', action, eventData);
    console.log('📊 GA Event Tracked:', action, eventData);
  }

  /**
   * Track page views
   * @param {string} page_title - Title of the page
   * @param {string} page_location - URL of the page
   */
  trackPageView(page_title, page_location) {
    if (!this.isEnabled) {
      console.log('📊 GA Page View (Dev):', { page_title, page_location });
      return;
    }

    window.gtag('config', this.trackingId, {
      page_title: page_title,
      page_location: page_location
    });

    console.log('📊 GA Page View Tracked:', { page_title, page_location });
  }

  /**
   * Track user authentication events
   */
  trackAuth = {
    signup: (method = 'email') => {
      this.trackEvent('sign_up', 'authentication', method);
    },
    
    login: (method = 'email') => {
      this.trackEvent('login', 'authentication', method);
    },
    
    logout: () => {
      this.trackEvent('logout', 'authentication');
    }
  };

  /**
   * Track investment game events
   */
  trackGame = {
    startGame: () => {
      this.trackEvent('game_start', 'gameplay');
    },
    
    makeInvestment: (asset_type, amount) => {
      this.trackEvent('investment_made', 'gameplay', asset_type, amount);
    },
    
    sellInvestment: (asset_type, amount) => {
      this.trackEvent('investment_sold', 'gameplay', asset_type, amount);
    },
    
    viewAnalytics: () => {
      this.trackEvent('view_analytics', 'engagement');
    },
    
    askQuestion: (question_type = 'portfolio') => {
      this.trackEvent('ask_question', 'engagement', question_type);
    },
    
    joinGroup: (group_code) => {
      this.trackEvent('join_group', 'social', group_code);
    },
    
    createGroup: () => {
      this.trackEvent('create_group', 'social');
    },
    
    viewGuide: () => {
      this.trackEvent('view_guide', 'education');
    },
    
    progressGuide: (step_number) => {
      this.trackEvent('guide_progress', 'education', `step_${step_number}`, step_number);
    },
    
    completeGuide: () => {
      this.trackEvent('guide_complete', 'education');
    },
    
    skipGuide: (step_number) => {
      this.trackEvent('guide_skip', 'education', `step_${step_number}`, step_number);
    }
  };

  /**
   * Track conversion events
   */
  trackConversion = {
    signupComplete: () => {
      this.trackEvent('conversion', 'signup_complete');
    },
    
    firstInvestment: (amount) => {
      this.trackEvent('conversion', 'first_investment', 'amount', amount);
    },
    
    portfolioMilestone: (milestone) => {
      this.trackEvent('conversion', 'portfolio_milestone', milestone);
    }
  };

  /**
   * Track user engagement
   */
  trackEngagement = {
    timeOnPage: (page_name, seconds) => {
      this.trackEvent('page_engagement', 'time_spent', page_name, seconds);
    },
    
    buttonClick: (button_name, page_name) => {
      this.trackEvent('button_click', 'ui_interaction', `${page_name}_${button_name}`);
    },
    
    modalOpen: (modal_name) => {
      this.trackEvent('modal_open', 'ui_interaction', modal_name);
    },
    
    errorEncountered: (error_type, page_name) => {
      this.trackEvent('error', 'user_experience', `${page_name}_${error_type}`);
    }
  };

  /**
   * Track educational content engagement
   */
  trackEducation = {
    tutorialStart: (tutorial_name) => {
      this.trackEvent('tutorial_start', 'education', tutorial_name);
    },
    
    tutorialComplete: (tutorial_name) => {
      this.trackEvent('tutorial_complete', 'education', tutorial_name);
    },
    
    helpAccessed: (help_topic) => {
      this.trackEvent('help_accessed', 'education', help_topic);
    }
  };
}

export default new GoogleAnalyticsService();